import { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { 
  CreditCard, Clock, Check, X, Eye, Euro,
  CheckCircle, XCircle, AlertCircle, User, Calendar,
  Download, FileText, Printer
} from 'lucide-react';

const STATUS_LABELS = {
  pending: 'Pendente',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  ready: 'Pronto',
  completed: 'Concluído',
};

const STATUS_COLORS = {
  pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  ready: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-gray-100 text-gray-600 border-gray-200',
};

const PAYMENT_LABELS = {
  pending: 'Aguarda Pagamento',
  paid: 'Pago',
};

const PREDEFINED_CARDS = [
  { key: 'antonio', name: 'Antonio de Oliveira da Silva' },
  { key: 'bernardeth', name: 'Bernardeth Luis Filipe' },
  { key: 'edson', name: 'Edson Canhata Francisco' },
  { key: 'elisangela', name: 'Elisângela de Jesus Santana Júlio' },
  { key: 'emanuel', name: 'Emanuel Watan Pascoal' },
  { key: 'gelcia', name: 'Gelcia Rafaela Pedro Santana' },
  { key: 'maria', name: 'Mariana Nwami Albino Manuel' },
  { key: 'mildo', name: 'Mildo Sergio Pascoal' },
  { key: 'neusa', name: 'Neusa Antónia Romão' },
  { key: 'paula', name: 'Paula Lopez Ngoge André' },
  { key: 'arleth', name: 'Arleth Nangassole Tomás Susso' },
  { key: 'taira', name: 'Taira da Graça Pedro Santana' },
  { key: 'vissolela', name: 'Vissolela D.M. Manuel Pascoal' },
];

export default function AdminConsularCards() {
  const { t } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedReq, setSelectedReq] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showManualGen, setShowManualGen] = useState(false);
  const [generating, setGenerating] = useState(null);

  const fetchRequests = () => {
    let url = '/consular-cards';
    if (filter) url += `?status=${filter}`;
    api.get(url).then(r => setRequests(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { fetchRequests(); }, [filter]);

  const handleUpdateStatus = async (id, status, notes = '') => {
    setUpdating(true);
    try {
      await api.put(`/consular-cards/${id}/status?status=${status}${notes ? `&admin_notes=${encodeURIComponent(notes)}` : ''}`);
      toast.success(`Pedido ${STATUS_LABELS[status].toLowerCase()}`);
      fetchRequests();
      setSelectedReq(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar pedido');
    } finally {
      setUpdating(false);
    }
  };

  const handleGenerateManualCard = async (personKey) => {
    setGenerating(personKey);
    try {
      const response = await api.get(`/admin/generate-card/${personKey}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CartaoConsular_${personKey}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Cartão de ${personKey} gerado com sucesso!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar cartão');
    } finally {
      setGenerating(null);
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const readyCount = requests.filter(r => r.status === 'ready').length;

  return (
    <div data-testid="admin-consular-cards">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cartões Consulares</h1>
          <p className="text-sm text-gray-500">{pendingCount} pendentes · {readyCount} prontos para levantamento</p>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowManualGen(!showManualGen)}
            className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white rounded-lg text-sm font-medium hover:bg-[#A30000] transition-colors"
            data-testid="btn-generate-manual"
          >
            <Printer size={16} />
            Gerar Cartão Manual
          </button>
          {showManualGen && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-10 py-2">
              <p className="px-4 py-2 text-xs text-gray-500 border-b">Cartões Pré-definidos:</p>
              {PREDEFINED_CARDS.map(card => (
                <button
                  key={card.key}
                  onClick={() => handleGenerateManualCard(card.key)}
                  disabled={generating === card.key}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  data-testid={`btn-gen-${card.key}`}
                >
                  <FileText size={14} className="text-gray-400" />
                  {generating === card.key ? 'A gerar...' : card.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-3 mb-6">
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20"
        >
          <option value="">Todos os estados</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {filter && (
          <button onClick={() => setFilter('')} className="text-sm text-gray-500 hover:text-red-600">
            Limpar filtro
          </button>
        )}
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <CreditCard size={32} className="mx-auto mb-3 text-gray-300" />
            <p>Nenhum pedido encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Referência</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Requerente</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Taxa</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Pagamento</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {requests.map(req => (
                  <tr key={req.id} className="hover:bg-gray-50 transition-colors" data-testid={`card-request-${req.id}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-[#CC0000]">{req.reference_code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{req.card_name}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{req.full_name}</p>
                        <p className="text-xs text-gray-400">{req.passport_number}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-bold text-gray-900">€{req.fee?.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[req.status]}`}>
                        {STATUS_LABELS[req.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`text-xs font-medium ${req.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>
                        {PAYMENT_LABELS[req.payment_status] || req.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setSelectedReq(req)} className="p-1.5 text-gray-400 hover:text-[#CC0000] rounded hover:bg-red-50" title="Ver detalhes">
                          <Eye size={14} />
                        </button>
                        {req.status === 'pending' && (
                          <>
                            <button onClick={() => handleUpdateStatus(req.id, 'approved')} className="p-1.5 text-gray-400 hover:text-green-600 rounded hover:bg-green-50" title="Aprovar">
                              <Check size={14} />
                            </button>
                            <button onClick={() => handleUpdateStatus(req.id, 'rejected')} className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50" title="Rejeitar">
                              <X size={14} />
                            </button>
                          </>
                        )}
                        {req.status === 'approved' && req.payment_status === 'paid' && (
                          <button onClick={() => handleUpdateStatus(req.id, 'ready')} className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50" title="Marcar como pronto">
                            <CheckCircle size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedReq && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="card-request-modal">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Detalhes do Pedido</h3>
              <button onClick={() => setSelectedReq(null)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">Referência</span>
                <span className="font-mono font-bold text-[#CC0000]">{selectedReq.reference_code}</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">Tipo</p>
                  <p className="text-sm font-medium">{selectedReq.card_name}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">Taxa</p>
                  <p className="text-sm font-bold text-[#CC0000]">€{selectedReq.fee?.toFixed(2)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">Estado</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedReq.status]}`}>
                    {STATUS_LABELS[selectedReq.status]}
                  </span>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">Pagamento</p>
                  <span className={`text-sm font-medium ${selectedReq.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>
                    {PAYMENT_LABELS[selectedReq.payment_status]}
                  </span>
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-2">Dados Pessoais</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Nome:</span> <span className="font-medium">{selectedReq.full_name}</span></div>
                  <div><span className="text-gray-500">Passaporte:</span> <span className="font-medium">{selectedReq.passport_number}</span></div>
                  <div><span className="text-gray-500">Nascimento:</span> <span className="font-medium">{selectedReq.birth_date}</span></div>
                  <div><span className="text-gray-500">Natural de:</span> <span className="font-medium">{selectedReq.birth_place}</span></div>
                  <div><span className="text-gray-500">Email:</span> <span className="font-medium">{selectedReq.email}</span></div>
                  <div><span className="text-gray-500">Telefone:</span> <span className="font-medium">{selectedReq.phone}</span></div>
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Morada em Espanha</p>
                <p className="text-sm">{selectedReq.address_spain}, {selectedReq.postal_code} {selectedReq.city}</p>
              </div>

              {selectedReq.pickup_appointment_id && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 mb-1 flex items-center gap-1"><Calendar size={12} /> Levantamento Agendado</p>
                  <p className="text-sm text-blue-800 font-medium">Consulte os agendamentos para detalhes</p>
                </div>
              )}

              <div className="text-xs text-gray-400">
                Criado em: {new Date(selectedReq.created_at).toLocaleString('pt')}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              {selectedReq.status === 'pending' && (
                <>
                  <button onClick={() => handleUpdateStatus(selectedReq.id, 'approved')} disabled={updating}
                    className="flex-1 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                    Aprovar
                  </button>
                  <button onClick={() => handleUpdateStatus(selectedReq.id, 'rejected')} disabled={updating}
                    className="flex-1 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                    Rejeitar
                  </button>
                </>
              )}
              {selectedReq.status === 'approved' && selectedReq.payment_status === 'paid' && (
                <button onClick={() => handleUpdateStatus(selectedReq.id, 'ready')} disabled={updating}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  Marcar como Pronto
                </button>
              )}
              {selectedReq.status === 'ready' && (
                <button onClick={() => handleUpdateStatus(selectedReq.id, 'completed')} disabled={updating}
                  className="flex-1 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 disabled:opacity-50">
                  Marcar como Concluído
                </button>
              )}
              <button onClick={() => setSelectedReq(null)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
